import React, { useEffect, useState } from "react";
import { useChat } from "../../../../context/contextChat";
import ChatLoader from "../../../loaders/Loader-6";
import MarkdownRenderer from "../utils/MarkdownRenderer";
import speakText from "../utils/textToSpeech";
import {
  getSecurityActionCategories,
  getSecurityContextLabel,
} from "../security-action-options";

const ChatBotBody = () => {
  const {
    chatMessages,
    selectedVulnerability,
    securityActionsAutoOpen,
    prompt,
    setPrompt,
    loading,
    messagesEndRef,
    textareaRef,
    showChat,
    handleClose,
    checkPrompt,
    handleSecurityAction,
    handleDownloadPdf,
    isDownloading
  } = useChat();

  const [isDarkMode, setIsDarkMode] = useState(true);
  const [showSecurityActions, setShowSecurityActions] = useState(false);
  const [securityActionSearch, setSecurityActionSearch] = useState("");
  const [selectedActionCategory, setSelectedActionCategory] = useState(null);
  const actionCategories = getSecurityActionCategories(selectedVulnerability);
  const findingContext = selectedVulnerability?.contextType === "finding";
  const securityContextLabel = getSecurityContextLabel(selectedVulnerability);

  const activeActionCategory = actionCategories.find(
    (category) => category.id === selectedActionCategory
  );
  const activeSecurityActions = activeActionCategory?.options || [];

  const filteredSecurityActions = activeSecurityActions.filter((action) =>
    action.label.toLowerCase().includes(securityActionSearch.trim().toLowerCase())
  );

  const selectActionCategory = (categoryId) => {
    setSelectedActionCategory(categoryId);
    setSecurityActionSearch("");
  };

  const selectSecurityAction = (action) => {
    setShowSecurityActions(false);
    setSecurityActionSearch("");
    handleSecurityAction(action.id, action.label);
  };

  const adjustHeight = () => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = "26px";
    textareaRef.current.style.height =
      Math.min(textareaRef.current.scrollHeight, 200) + "px";
  };

  /* ---------------- AUTO SCROLL ---------------- */
  useEffect(() => {
    const timer = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 40);
    return () => clearTimeout(timer);
  }, [chatMessages, loading]);

  useEffect(() => {
    adjustHeight();
  }, [prompt]);

  useEffect(() => {
    if (selectedVulnerability) {
      setShowSecurityActions(Boolean(securityActionsAutoOpen));
      setSecurityActionSearch("");
      setSelectedActionCategory(
        findingContext && securityActionsAutoOpen ? "web-finding" : null
      );
    }
  }, [findingContext, securityActionsAutoOpen, selectedVulnerability]);

  useEffect(() => {
    if (!showSecurityActions) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setShowSecurityActions(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [showSecurityActions]);

  if (!showChat) return null;

  const theme = {
    bgOverlay: isDarkMode ? "rgba(0, 0, 0, 0.75)" : "rgba(15, 23, 42, 0.4)",
    cardBg: isDarkMode ? "#1e1e1e" : "#ffffff",
    cardBoxShadow: isDarkMode ? "0 25px 60px -15px rgba(0, 0, 0, 0.6)" : "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
    textMain: isDarkMode ? "#e4e4e7" : "#1e293b",
    textMuted: isDarkMode ? "#a1a1aa" : "#64748b",
    bubbleBg: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "#f1f5f9",
    bubbleText: isDarkMode ? "#f4f4f5" : "#0f172a",
    borderLine: isDarkMode ? "2px solid #4f46e5" : "2px solid #3b82f6",
    inputPodBg: isDarkMode ? "#18181b" : "#f8fafc",
    inputPodBorder: isDarkMode ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
    inputText: isDarkMode ? "#ffffff" : "#0f172a",
    inputPlaceholder: isDarkMode ? "#52525b" : "#94a3b8",
    sendBtnBg: isDarkMode ? "#4c1d95" : "#2563eb",
    sendBtnDisabled: isDarkMode ? "#27272a" : "#e2e8f0",
    sendBtnIcon: isDarkMode ? "#71717a" : "#cbd5e1"
  };

  return (
    <div 
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{
        zIndex: 1060,
        backgroundColor: theme.bgOverlay,
        backdropFilter: "blur(16px)",
        animation: "fadeIn 0.25s ease-out",
        transition: "background-color 0.3s ease"
      }}
    >
      <div 
        className="d-flex flex-column border-0 p-4 w-100 mx-3 mx-md-0 position-relative"
        style={{
          maxWidth: "880px",
          height: "85vh",
          maxHeight: "800px",
          backgroundColor: theme.cardBg,
          borderRadius: "28px",
          boxShadow: theme.cardBoxShadow,
          transition: "background-color 0.3s ease, box-shadow 0.3s ease"
        }}
      >
        
        {/* UPPER CONTROL TIER */}
        <div className="position-absolute d-flex align-items-center gap-2" style={{ top: "20px", right: "25px", zIndex: 10 }}>
          <button
            type="button"
            className={`btn btn-sm rounded-pill px-2.5 py-1 d-flex align-items-center gap-1.5 border transition-all ${
              isDarkMode ? "btn-dark border-secondary text-light" : "btn-light border-muted text-dark"
            }`}
            style={{ fontSize: "0.78rem", fontWeight: "500" }}
            onClick={() => setIsDarkMode(!isDarkMode)}
          >
            <i className={`bi ${isDarkMode ? "bi-moon-stars-fill text-warning" : "bi-sun-fill text-danger"}`} />
            <span>{isDarkMode ? "Dark Interface" : "Light Interface"}</span>
          </button>

          <button 
            type="button" 
            className={`btn btn-link p-1 text-decoration-none transition-all ${isDarkMode ? "text-secondary hover-white-text" : "text-muted hover-dark-text"}`}
            onClick={handleClose}
          >
            <i className="bi bi-x-lg" style={{ fontSize: "1.05rem" }} />
          </button>
        </div>

        {/* MESSAGES LAYER AREA PANEL */}
        <div 
          className="flex-grow-1 overflow-y-auto pe-2 pt-5 mb-3 style-scrollbar"
          style={{ backgroundColor: "transparent" }}
        >
          {!chatMessages.length && !loading && (
            <div className="h-100 d-flex flex-column justify-content-center align-items-center text-center style-fade-in">
              <div className="mb-3" style={{ color: theme.textMuted }}>
                <i className="bi bi-terminal" style={{ fontSize: "2.5rem" }} />
              </div>
              <h6 className="fw-bold mb-1" style={{ color: theme.textMain }}>SAVE Core Intelligence Unit</h6>
              <p className="small px-3" style={{ maxWidth: "340px", color: theme.textMuted }}>
                Input raw configuration data payloads or analyze system event parameters seamlessly.
              </p>
            </div>
          )}

          {chatMessages
            .filter(m => m?.text && m?.type)
            .map((msg, i) => {
              const isUser = msg.type === "user";
              return (
                <div key={i} className={`d-flex ${isUser ? "justify-content-end" : "justify-content-start"} mb-4`}>
                  <div className="max-w-85 position-relative">
                    <div 
                      className="px-3.5 py-2.5 rounded-4 shadow-sm word-type-animation"
                      style={{
                        backgroundColor: theme.bubbleBg,
                        color: isUser ? theme.bubbleText : theme.textMain,
                        fontFamily: isUser ? "monospace" : "inherit",
                        whiteSpace: "pre-wrap",
                        fontSize: "0.92rem",
                        borderLeft: isUser ? "none" : theme.borderLine
                      }}
                    >
                      <MarkdownRenderer markdownText={msg.text} />
                    </div>

                    {!isUser && (
                      <div className="d-flex gap-2 mt-1.5 px-2 justify-content-start opacity-50 hover-opacity-100">
                        <button
                          type="button"
                          className="btn btn-sm border-0 p-0 text-secondary bg-transparent"
                          style={{ width: "24px", height: "24px" }}
                          onClick={() => speakText(msg.text)}
                        >
                          <i className="bi bi-volume-up-fill" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm border-0 p-0 text-secondary bg-transparent"
                          disabled={isDownloading !== null}
                          onClick={() => handleDownloadPdf(msg.text, i)}
                        >
                          {isDownloading === i ? (
                            <span className="spinner-border spinner-border-sm" style={{ width: '10px', height: '10px' }} />
                          ) : (
                            <i className="bi bi-file-earmark-arrow-down-fill" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

          {/* ASYNC API API PENDING PROCESSING VIEW */}
          {loading && (
            <div className="d-flex align-items-center gap-2 py-3 px-1 small">
              <ChatLoader />
              <span style={{ color: theme.textMuted, fontSize: '0.8rem' }}>Processing environment matrix...</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* INTEGRATED DISPATCH INPUT SYSTEM */}
        <div 
          className="d-flex flex-column px-3 pt-3 pb-2 position-relative"
          style={{ 
            backgroundColor: theme.inputPodBg,
            borderRadius: "20px",
            border: theme.inputPodBorder,
            transition: "all 0.3s ease"
          }}
        >
          {selectedVulnerability && (
            <div className="mb-2 style-fade-in">
              <button
                type="button"
                className="security-action-trigger w-100 d-flex align-items-center justify-content-between gap-3 text-start"
                aria-expanded={showSecurityActions}
                aria-controls="security-action-selector"
                onClick={() => setShowSecurityActions((current) => !current)}
                style={{
                  color: theme.textMain,
                  backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.045)" : "#ffffff",
                  borderColor: isDarkMode ? "rgba(255, 255, 255, 0.09)" : "#dbe3ee",
                }}
              >
                <span className="d-flex align-items-center gap-2 min-width-0">
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{
                      width: "30px",
                      height: "30px",
                      backgroundColor: isDarkMode ? "#312e81" : "#dbeafe",
                      color: isDarkMode ? "#e0e7ff" : "#1e40af",
                    }}
                  >
                    <i className="bi bi-shield-check" />
                  </span>
                  <span className="min-width-0">
                    <span className="d-block small fw-semibold">Risk Analysis</span>
                    <span className="d-block text-truncate" style={{ color: theme.textMuted, fontSize: "0.72rem" }}>
                      {activeActionCategory?.label || securityContextLabel || "Choose analysis type"}
                    </span>
                  </span>
                </span>

                <span className="d-flex align-items-center gap-2 flex-shrink-0">
                  <span
                    className="badge rounded-pill"
                    style={{ backgroundColor: isDarkMode ? "#312e81" : "#dbeafe", color: isDarkMode ? "#e0e7ff" : "#1e40af" }}
                  >
                    {activeActionCategory?.options.length || actionCategories.length}
                  </span>
                  <i className={`bi ${showSecurityActions ? "bi-chevron-down" : "bi-chevron-up"}`} />
                </span>
              </button>

              {showSecurityActions && (
                <div
                  id="security-action-selector"
                  className="security-action-popover style-fade-in"
                  style={{
                    color: theme.textMain,
                    backgroundColor: theme.inputPodBg,
                    borderColor: isDarkMode ? "rgba(255, 255, 255, 0.12)" : "#dbe3ee",
                    boxShadow: isDarkMode ? "0 24px 60px rgba(0, 0, 0, 0.58)" : "0 24px 60px rgba(15, 23, 42, 0.2)",
                  }}
                >
                  <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                    <div className="d-flex align-items-start gap-2 min-width-0">
                      {activeActionCategory && actionCategories.length > 1 && (
                        <button
                          type="button"
                          className={`btn btn-sm border-0 p-1 ${isDarkMode ? "text-secondary" : "text-muted"}`}
                          aria-label="Back to risk estimation types"
                          onClick={() => {
                            setSelectedActionCategory(null);
                            setSecurityActionSearch("");
                          }}
                        >
                          <i className="bi bi-arrow-left" />
                        </button>
                      )}
                      <div className="min-width-0">
                        <div className="small fw-semibold">
                          {activeActionCategory?.label || "Choose risk estimation type"}
                        </div>
                        <div className="text-truncate" style={{ color: theme.textMuted, fontSize: "0.74rem" }}>
                          {securityContextLabel} · {activeActionCategory?.options.length || actionCategories.length} options
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={`btn btn-sm border-0 p-1 flex-shrink-0 ${isDarkMode ? "text-secondary" : "text-muted"}`}
                      aria-label="Close risk analysis selector"
                      onClick={() => setShowSecurityActions(false)}
                    >
                      <i className="bi bi-x-lg" />
                    </button>
                  </div>

                  {!activeActionCategory ? (
                    <div className="risk-category-grid" role="group" aria-label="Risk estimation types">
                      {actionCategories.map((category) => (
                        <button
                          key={category.id}
                          type="button"
                          className="risk-category-button d-flex align-items-center gap-3 text-start"
                          onClick={() => selectActionCategory(category.id)}
                          style={{
                            color: theme.textMain,
                            backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.045)" : "#ffffff",
                            borderColor: isDarkMode ? "rgba(255, 255, 255, 0.09)" : "#dbe3ee",
                          }}
                        >
                          <span
                            className="risk-category-icon d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                            style={{
                              backgroundColor: isDarkMode ? "#312e81" : "#dbeafe",
                              color: isDarkMode ? "#e0e7ff" : "#1e40af",
                            }}
                          >
                            <i className={`bi ${category.icon}`} />
                          </span>
                          <span className="min-width-0 flex-grow-1">
                            <span className="d-block fw-semibold small">{category.label}</span>
                            <span className="d-block mt-1" style={{ color: theme.textMuted, fontSize: "0.72rem" }}>
                              {category.description}
                            </span>
                            <span className="d-block mt-2" style={{ color: isDarkMode ? "#a5b4fc" : "#2563eb", fontSize: "0.72rem" }}>
                              {category.options.length} available actions
                            </span>
                          </span>
                          <i className="bi bi-chevron-right flex-shrink-0" style={{ color: theme.textMuted }} />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <>
                      <div className="security-action-search d-flex align-items-center gap-2 mb-3 px-3">
                        <i className="bi bi-search" style={{ color: theme.textMuted }} />
                        <input
                          type="search"
                          className="form-control border-0 shadow-none bg-transparent p-0"
                          value={securityActionSearch}
                          onChange={(event) => setSecurityActionSearch(event.target.value)}
                          placeholder={`Search ${activeActionCategory.label.toLowerCase()} actions`}
                          aria-label={`Search ${activeActionCategory.label} actions`}
                          style={{ color: theme.inputText, "--placeholder-color": theme.inputPlaceholder }}
                        />
                      </div>

                      <div
                        className="security-action-grid style-scrollbar pe-1"
                        role="group"
                        aria-label={`${activeActionCategory.label} actions for ${securityContextLabel}`}
                      >
                        {filteredSecurityActions.map((action) => {
                          const optionNumber = activeSecurityActions.findIndex((option) => option.id === action.id) + 1;
                          return (
                            <button
                              key={action.id}
                              type="button"
                              className="security-action-button d-flex align-items-center gap-2 text-start"
                              disabled={loading}
                              onClick={() => selectSecurityAction(action)}
                              title={action.label}
                              style={{
                                color: theme.textMain,
                                backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.045)" : "#ffffff",
                                borderColor: isDarkMode ? "rgba(255, 255, 255, 0.09)" : "#dbe3ee",
                              }}
                            >
                              <span
                                className="d-inline-flex align-items-center justify-content-center flex-shrink-0 rounded-circle"
                                style={{
                                  width: "24px",
                                  height: "24px",
                                  fontSize: "0.68rem",
                                  backgroundColor: isDarkMode ? "#312e81" : "#dbeafe",
                                  color: isDarkMode ? "#e0e7ff" : "#1e40af",
                                }}
                              >
                                {optionNumber}
                              </span>
                              <span className="text-truncate">{action.label}</span>
                            </button>
                          );
                        })}
                      </div>

                      {!filteredSecurityActions.length && (
                        <div className="text-center py-4 small" style={{ color: theme.textMuted }}>
                          No {activeActionCategory.label.toLowerCase()} actions match your search.
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          <textarea
            ref={textareaRef}
            value={prompt}
            disabled={loading}
            onChange={(e) => {
              setPrompt(e.target.value);
              adjustHeight();
            }}
            rows={1}
            className="form-control border-0 p-0 shadow-none bg-transparent"
            placeholder="Ask anything"
            style={{
              maxHeight: "200px",
              minHeight: "36px",
              lineHeight: "1.6",
              outline: "none",
              fontSize: "0.95rem",
              resize: "none",
              color: theme.inputText,
              "--placeholder-color": theme.inputPlaceholder
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (prompt.trim()) checkPrompt(prompt);
              }
            }}
          />

          <div className="d-flex align-items-center justify-content-between mt-2 pt-1 border-top" style={{ borderColor: isDarkMode ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.04)" }}>
            <div>
              <button type="button" className={`btn btn-link p-0 text-decoration-none ${isDarkMode ? "text-secondary hover-white-text" : "text-muted hover-dark-text"}`}>
                <i className="bi bi-plus-lg fs-5" />
              </button>
            </div>

            <div className="d-flex align-items-center gap-3">
              <div className="dropdown">
                <button className={`btn btn-link p-0 text-decoration-none d-flex align-items-center gap-1 small ${isDarkMode ? "text-secondary hover-white-text" : "text-muted hover-dark-text"}`} type="button" style={{ fontSize: "0.85rem" }}>
                  Flash <i className="bi bi-chevron-down small" style={{ fontSize: "0.7rem" }} />
                </button>
              </div>

              <button type="button" className={`btn btn-link p-0 text-decoration-none ${isDarkMode ? "text-secondary hover-white-text" : "text-muted hover-dark-text"}`}>
                <i className="bi bi-mic fs-5" />
              </button>

              <button
                type="button"
                className="btn d-flex align-items-center justify-content-center rounded-circle border-0 p-0"
                style={{ 
                  width: "34px", 
                  height: "34px",
                  backgroundColor: !prompt.trim() || loading ? theme.sendBtnDisabled : theme.sendBtnBg,
                  color: !prompt.trim() || loading ? theme.sendBtnIcon : "#ffffff",
                  cursor: !prompt.trim() || loading ? "not-allowed" : "pointer"
                }}
                disabled={loading || !prompt.trim()}
                onClick={() => checkPrompt(prompt)}
              >
                <i className="bi bi-arrow-up-short fs-3 text-white" />
              </button>
            </div>
          </div>
        </div>

      </div>

      <style>{`
        .style-scrollbar::-webkit-scrollbar { width: 5px; }
        .style-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .style-scrollbar::-webkit-scrollbar-thumb { background: ${isDarkMode ? "#3f3f46" : "#cbd5e1"}; border-radius: 10px; }
        .style-scrollbar::-webkit-scrollbar-thumb:hover { background: ${isDarkMode ? "#52525b" : "#94a3b8"}; }
        .max-w-85 { max-width: 85%; }
        .gap-1\\.5 { gap: 0.375rem; }
        .px-2\\.5 { padding-left: 0.625rem; padding-right: 0.625rem; }
        
        textarea::placeholder { color: var(--placeholder-color) !important; opacity: 1; }
        .hover-white-text:hover { color: #ffffff !important; }
        .hover-dark-text:hover { color: #0f172a !important; }

        .min-width-0 { min-width: 0; }

        .security-action-trigger {
          min-height: 48px;
          padding: 7px 10px;
          border-width: 1px;
          border-style: solid;
          border-radius: 13px;
          transition: border-color 0.15s ease, background-color 0.15s ease;
        }

        .security-action-trigger:hover {
          border-color: ${isDarkMode ? "#6366f1" : "#3b82f6"} !important;
        }

        .security-action-popover {
          position: absolute;
          left: 0;
          right: 0;
          bottom: calc(100% + 10px);
          z-index: 20;
          max-height: min(56vh, 470px);
          padding: 16px;
          border-width: 1px;
          border-style: solid;
          border-radius: 18px;
          overflow: hidden;
        }

        .risk-category-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }

        .risk-category-button {
          min-width: 0;
          min-height: 132px;
          padding: 14px;
          border-width: 1px;
          border-style: solid;
          border-radius: 14px;
          transition: transform 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
        }

        .risk-category-button:hover {
          transform: translateY(-2px);
          border-color: ${isDarkMode ? "#6366f1" : "#3b82f6"} !important;
        }

        .risk-category-button:focus-visible {
          outline: 2px solid ${isDarkMode ? "#818cf8" : "#2563eb"};
          outline-offset: 2px;
        }

        .risk-category-icon {
          width: 42px;
          height: 42px;
          font-size: 1.05rem;
        }

        .security-action-search {
          min-height: 42px;
          border: 1px solid ${isDarkMode ? "rgba(255, 255, 255, 0.09)" : "#dbe3ee"};
          border-radius: 11px;
          background: ${isDarkMode ? "rgba(255, 255, 255, 0.035)" : "#f8fafc"};
        }

        .security-action-search input::placeholder {
          color: var(--placeholder-color) !important;
        }

        .security-action-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
          max-height: min(38vh, 310px);
          overflow-y: auto;
        }

        .security-action-button {
          min-width: 0;
          min-height: 42px;
          padding: 7px 10px;
          border-width: 1px;
          border-style: solid;
          border-radius: 11px;
          font-size: 0.79rem;
          transition: transform 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
        }

        .security-action-button:hover:not(:disabled) {
          transform: translateY(-1px);
          border-color: ${isDarkMode ? "#6366f1" : "#3b82f6"} !important;
        }

        .security-action-button:focus-visible {
          outline: 2px solid ${isDarkMode ? "#818cf8" : "#2563eb"};
          outline-offset: 1px;
        }

        .security-action-button:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }

        @media (max-width: 576px) {
          .risk-category-grid { grid-template-columns: 1fr; }
          .risk-category-button { min-height: 112px; }
          .security-action-grid { grid-template-columns: 1fr; }
          .security-action-popover {
            position: fixed;
            left: 12px;
            right: 12px;
            bottom: 12px;
            max-height: 72vh;
            border-radius: 20px;
          }
        }
        
        /* Ultra-Fast Clean Response Render Slide Animation */
        .word-type-animation {
          animation: textRenderFast 0.18s cubic-bezier(0.1, 0.9, 0.2, 1) forwards;
        }

        @keyframes textRenderFast {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .style-fade-in { animation: fadeIn 0.2s ease-out forwards; }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default ChatBotBody;
